import React, { useEffect, useState } from "react";
import { currency } from "../App";
import { adminGet, adminPost } from "../common/api/api";

const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    code: "",
    discount: "",
    discountType: "percentage",
    minAmount: "",
    maxUses: "100",
    expiryDate: "",
  });

  const fetchCoupons = async () => {
    setLoading(true);
    const data = await adminGet("/allcoupons");
    setCoupons(Array.isArray(data) ? data : []);
    setLoading(false);
  };

  useEffect(() => { fetchCoupons(); }, []);

  const addCoupon = async () => {
    if (!form.code || !form.discount) { alert("Code and discount are required"); return; }
    const data = await adminPost("/addcoupon", {
      ...form,
      discount: parseFloat(form.discount),
      minAmount: parseFloat(form.minAmount) || 0,
      maxUses: parseInt(form.maxUses) || 100,
    });
    if (data.success) {
      setForm({ code: "", discount: "", discountType: "percentage", minAmount: "", maxUses: "100", expiryDate: "" });
      setShowForm(false);
      fetchCoupons();
    } else {
      alert(data.error || "Failed to add coupon");
    }
  };

  const toggleCoupon = async (id) => {
    await adminPost("/togglecoupon", { id });
    fetchCoupons();
  };

  const deleteCoupon = async (id) => {
    if (!window.confirm("Delete this coupon?")) return;
    await adminPost("/deletecoupon", { id });
    fetchCoupons();
  };

  const inputClass = "bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-[#e2e8f0] placeholder-[#4a5568] focus:outline-none focus:border-[#00d4ff]/60 text-sm w-full";

  const isExpired = (date) => date && new Date(date) < new Date();

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Coupons</h1>
          <p className="text-sm text-[#4a5568] mt-0.5">{coupons.filter(c => c.active).length} active coupons</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 bg-gradient-to-r from-[#00d4ff] to-[#0099cc] text-[#060c18] font-bold rounded-lg text-sm hover:opacity-90 transition-opacity cursor-pointer"
        >
          + Add Coupon
        </button>
      </div>

      {/* Add Coupon Form */}
      {showForm && (
        <div className="mb-5 p-5 bg-white/[0.03] border border-white/[0.08] rounded-xl">
          <h3 className="text-sm font-semibold text-white mb-4">New Coupon</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
            <div>
              <label className="block text-xs text-[#a0aec0] mb-1">Coupon Code *</label>
              <input className={inputClass} placeholder="e.g. SAVE20" value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} />
            </div>
            <div>
              <label className="block text-xs text-[#a0aec0] mb-1">Discount *</label>
              <input className={inputClass} type="number" placeholder="20" value={form.discount} onChange={e => setForm({ ...form, discount: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs text-[#a0aec0] mb-1">Type</label>
              <select className={inputClass} style={{ background: '#0d1527' }} value={form.discountType} onChange={e => setForm({ ...form, discountType: e.target.value })}>
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed Amount ({currency})</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-[#a0aec0] mb-1">Min Order Amount</label>
              <input className={inputClass} type="number" placeholder="0" value={form.minAmount} onChange={e => setForm({ ...form, minAmount: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs text-[#a0aec0] mb-1">Max Uses</label>
              <input className={inputClass} type="number" placeholder="100" value={form.maxUses} onChange={e => setForm({ ...form, maxUses: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs text-[#a0aec0] mb-1">Expiry Date</label>
              <input className={inputClass} type="date" value={form.expiryDate} onChange={e => setForm({ ...form, expiryDate: e.target.value })} style={{ colorScheme: 'dark' }} />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={addCoupon} className="px-4 py-2 bg-[rgba(0,212,255,0.15)] text-[#00d4ff] border border-[rgba(0,212,255,0.3)] rounded-lg text-sm hover:bg-[rgba(0,212,255,0.25)] transition-colors cursor-pointer">Create Coupon</button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 text-[#4a5568] border border-white/[0.08] rounded-lg text-sm hover:border-white/[0.2] transition-colors cursor-pointer">Cancel</button>
          </div>
        </div>
      )}

      {/* Coupon List */}
      {loading ? (
        <div className="text-[#4a5568] py-16 text-center">Loading coupons...</div>
      ) : coupons.length === 0 ? (
        <div className="py-16 text-center text-[#4a5568]">No coupons yet. Create one above.</div>
      ) : (
        <div className="space-y-3">
          {coupons.map((coupon) => {
            const expired = isExpired(coupon.expiryDate);
            const usagePercent = coupon.maxUses > 0 ? Math.min(100, Math.round((coupon.usedCount / coupon.maxUses) * 100)) : 0;
            return (
              <div key={coupon._id} className={`p-4 rounded-xl border transition-all ${!coupon.active || expired ? "border-white/[0.05] bg-white/[0.01] opacity-60" : "border-white/[0.08] bg-white/[0.02]"}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-[#00d4ff] font-mono font-bold text-base tracking-widest">{coupon.code}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[rgba(0,212,255,0.1)] text-[#00d4ff] border border-[rgba(0,212,255,0.25)] font-bold">
                        {coupon.discount}{coupon.discountType === "percentage" ? "%" : currency} OFF
                      </span>
                      {!coupon.active && <span className="text-xs px-2 py-0.5 rounded-full bg-[#4a5568]/20 text-[#4a5568] border border-[#4a5568]/30">INACTIVE</span>}
                      {expired && <span className="text-xs px-2 py-0.5 rounded-full bg-red-400/10 text-red-400 border border-red-400/30">EXPIRED</span>}
                    </div>
                    <div className="flex gap-4 text-xs text-[#4a5568] flex-wrap">
                      {coupon.minAmount > 0 && <span>Min order: {currency}{coupon.minAmount}</span>}
                      {coupon.expiryDate && <span>Expires: {new Date(coupon.expiryDate).toLocaleDateString()}</span>}
                      <span>Used: {coupon.usedCount || 0}/{coupon.maxUses}</span>
                    </div>
                    {/* Usage bar */}
                    <div className="mt-2 h-1.5 bg-white/[0.06] rounded-full w-48 max-w-full">
                      <div className="h-full rounded-full bg-gradient-to-r from-[#00d4ff] to-[#7c3aed] transition-all" style={{ width: `${usagePercent}%` }} />
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => toggleCoupon(coupon._id)}
                      className={`text-xs px-3 py-1.5 rounded-lg border transition-all cursor-pointer ${
                        coupon.active ? "text-yellow-400 border-yellow-400/30 hover:bg-yellow-400/10" : "text-green-400 border-green-400/30 hover:bg-green-400/10"
                      }`}
                    >
                      {coupon.active ? "Disable" : "Enable"}
                    </button>
                    <button
                      onClick={() => deleteCoupon(coupon._id)}
                      className="text-xs px-3 py-1.5 rounded-lg border border-red-500/20 text-red-400 hover:bg-red-500/10 transition-all cursor-pointer"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminCoupons;
